import * as React from 'react';
import { FC, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { Platform, View } from 'react-native';

import { determineEmptyStateCategory } from '@fhs/store-locator';
import { useAuthContext } from 'state/auth';

import { DEFAULT_CONTENT, EMPTY_STATES_CONTENT } from './constants';
import { EmptyStateAction } from './empty-state-action';
import { BodyText, HeadingText, IconWrapper, Wrapper } from './store-locator-empty-states.styled';
import { IStoreLocatorEmptyStatesProps } from './types';

/**
 *
 * StoreLocatorEmptyStates enables displaying different empty state messages for store locator tabs
 *
 */
export const StoreLocatorEmptyStates: FC<
  React.PropsWithChildren<IStoreLocatorEmptyStatesProps>
> = ({ tab, locationPermissionsGranted, currentTabError, coordinatesAvailable }) => {
  const { isAuthenticated } = useAuthContext();
  const { formatMessage } = useIntl();

  const {
    icon: Icon,
    titleId,
    bodyId,
    action,
  } = useMemo(() => {
    const category = determineEmptyStateCategory({
      tab,
      isAuthenticated,
      locationPermissionsGranted,
      currentTabError,
      coordinatesAvailable,
    });
    return EMPTY_STATES_CONTENT[tab]?.[category] || DEFAULT_CONTENT;
  }, [currentTabError, isAuthenticated, locationPermissionsGranted, tab, coordinatesAvailable]);
  const title = formatMessage({ id: titleId });
  const body = formatMessage({ id: bodyId });
  const summaryLabel = `${title}. ${body}`;

  return (
    <Wrapper>
      {Icon && (
        <IconWrapper>
          <Icon />
        </IconWrapper>
      )}
      {Platform.OS === 'web' ? (
        <View
          accessibilityLabel={summaryLabel}
          tabIndex={0}
          {...({ role: 'region' } as React.ComponentProps<typeof View>)}
        >
          <HeadingText>{title}</HeadingText>
          <BodyText>{body}</BodyText>
        </View>
      ) : (
        <>
          <HeadingText>{title}</HeadingText>
          <BodyText>{body}</BodyText>
        </>
      )}
      {action && <EmptyStateAction action={action} />}
    </Wrapper>
  );
};
